import React, { useEffect, useState, useRef } from 'react';
import api from '../lib/api';
import { Plus, Trash2, ImagePlus, Type, Move, ArrowLeft, LayoutGrid, FolderOpen, Folder, Edit2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useTheme } from '../contexts/ThemeContext';
import { toast } from 'sonner';

// Template definitions: 12-column × 12-row grid
const TEMPLATES = [
  { id: 'classic', name: 'Clásico 3×3', desc: '9 espacios iguales',
    slots: Array.from({ length: 9 }, (_, i) => ({ x: (i % 3) * 4, y: Math.floor(i / 3) * 4, w: 4, h: 4 })) },
  { id: 'hero', name: 'Hero', desc: '1 grande + 4 pequeños',
    slots: [ { x: 0, y: 0, w: 8, h: 8 }, { x: 8, y: 0, w: 4, h: 4 }, { x: 8, y: 4, w: 4, h: 4 }, { x: 0, y: 8, w: 6, h: 4 }, { x: 6, y: 8, w: 6, h: 4 } ] },
  { id: 'mosaic', name: 'Mosaico', desc: 'Asimétrico dinámico',
    slots: [ { x: 0, y: 0, w: 4, h: 4 }, { x: 4, y: 0, w: 8, h: 3 }, { x: 4, y: 3, w: 5, h: 5 }, { x: 9, y: 3, w: 3, h: 5 }, { x: 0, y: 4, w: 4, h: 4 }, { x: 0, y: 8, w: 6, h: 4 }, { x: 6, y: 8, w: 6, h: 4 } ] },
  { id: 'columns', name: 'Columnas', desc: '3 columnas Pinterest',
    slots: [ { x: 0, y: 0, w: 4, h: 5 }, { x: 4, y: 0, w: 4, h: 3 }, { x: 8, y: 0, w: 4, h: 4 }, { x: 4, y: 3, w: 4, h: 5 }, { x: 8, y: 4, w: 4, h: 4 }, { x: 0, y: 5, w: 4, h: 4 }, { x: 4, y: 8, w: 4, h: 4 }, { x: 8, y: 8, w: 4, h: 4 }, { x: 0, y: 9, w: 4, h: 3 } ] },
  { id: 'story', name: 'Historia', desc: 'Banner + rejilla',
    slots: [ { x: 0, y: 0, w: 12, h: 4 }, { x: 0, y: 4, w: 4, h: 4 }, { x: 4, y: 4, w: 4, h: 4 }, { x: 8, y: 4, w: 4, h: 4 }, { x: 0, y: 8, w: 6, h: 4 }, { x: 6, y: 8, w: 6, h: 4 } ] },
  { id: 'vertical', name: 'Verticales', desc: '4 columnas altas',
    slots: [ { x: 0, y: 0, w: 3, h: 8 }, { x: 3, y: 0, w: 3, h: 8 }, { x: 6, y: 0, w: 3, h: 8 }, { x: 9, y: 0, w: 3, h: 8 }, { x: 0, y: 8, w: 6, h: 4 }, { x: 6, y: 8, w: 6, h: 4 } ] },
  { id: 'minimal', name: 'Minimalista', desc: '4 espacios grandes',
    slots: [ { x: 0, y: 0, w: 6, h: 6 }, { x: 6, y: 0, w: 6, h: 6 }, { x: 0, y: 6, w: 6, h: 6 }, { x: 6, y: 6, w: 6, h: 6 } ] },
];

const SAMPLE_GRADIENTS = [
  'linear-gradient(135deg, #FFB88C, #DE6262)',
  'linear-gradient(135deg, #A8EDEA, #FED6E3)',
  'linear-gradient(135deg, #667EEA, #764BA2)',
  'linear-gradient(135deg, #F093FB, #F5576C)',
  'linear-gradient(135deg, #FAD961, #F76B1C)',
  'linear-gradient(135deg, #4FACFE, #00F2FE)',
  'linear-gradient(135deg, #43E97B, #38F9D7)',
  'linear-gradient(135deg, #FA709A, #FEE140)',
  'linear-gradient(135deg, #30CFD0, #330867)',
];

const TemplatePreview = ({ slots, color, bg, active, samples = true }) => (
  <div className="relative w-full rounded-md overflow-hidden" style={{ aspectRatio: '1 / 1', background: bg, border: active ? `2px solid ${color}` : '1px solid transparent' }}>
    {slots.map((s, i) => (
      <div key={i} className="absolute overflow-hidden p-[2px]" style={{
        left: `${(s.x / 12) * 100}%`, top: `${(s.y / 12) * 100}%`,
        width: `${(s.w / 12) * 100}%`, height: `${(s.h / 12) * 100}%`,
      }}>
        <div className="w-full h-full rounded-sm" style={{
          background: samples ? SAMPLE_GRADIENTS[i % SAMPLE_GRADIENTS.length] : color,
          opacity: samples ? 0.85 : 0.35,
          border: `1px solid ${color}60`,
        }} />
      </div>
    ))}
  </div>
);

const VisionBoard = () => {
  const [boards, setBoards] = useState([]);
  const [boardPreviews, setBoardPreviews] = useState({}); // board_id -> items for preview
  const [activeBoard, setActiveBoard] = useState(null);
  const [items, setItems] = useState([]);
  const [view, setView] = useState('folder');
  const [showNewBoard, setShowNewBoard] = useState(false);
  const [newBoardName, setNewBoardName] = useState('');
  const [slotIndex, setSlotIndex] = useState(null);
  const [newType, setNewType] = useState('image');
  const [newContent, setNewContent] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [editingBoardId, setEditingBoardId] = useState(null);
  const [editBoardName, setEditBoardName] = useState('');
  const { theme } = useTheme();
  const boardRef = useRef(null);
  const [dragging, setDragging] = useState(null);
  // Collage mode (no gaps/borders) toggle, persisted per board id
  const [collageMode, setCollageMode] = useState(false);
  useEffect(() => {
    if (activeBoard?.id) {
      try { setCollageMode(localStorage.getItem(`teko-vb-collage-${activeBoard.id}`) === '1'); } catch { setCollageMode(false); }
    }
  }, [activeBoard?.id]);
  const toggleCollageMode = () => {
    if (!activeBoard?.id) return;
    const next = !collageMode;
    setCollageMode(next);
    localStorage.setItem(`teko-vb-collage-${activeBoard.id}`, next ? '1' : '0');
  };
  // Toggle fit mode (cover/contain) per item
  const toggleFitMode = async (id) => {
    const it = items.find(x => x.id === id); if (!it) return;
    const pos = { ...(it.position || {}) };
    pos.fit_mode = pos.fit_mode === 'contain' ? 'cover' : 'contain';
    setItems(prev => prev.map(x => x.id === id ? { ...x, position: pos } : x));
    try { await api.put('/vision-board/positions', { positions: [{ id, ...pos }] }); } catch (err) { console.debug(err); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchBoards(); }, []);

  const fetchBoards = async () => {
    try {
      const { data } = await api.get('/vision-boards');
      setBoards(data);
      // Fetch preview items for each board in parallel
      const previews = {};
      await Promise.all(data.map(async (b) => {
        try {
          const r = await api.get(`/vision-board?board_id=${b.id}`);
          previews[b.id] = r.data;
        } catch { previews[b.id] = []; }
      }));
      setBoardPreviews(previews);
    } catch (err) { console.debug(err); }
  };
  const fetchItems = async (boardId) => { try { setItems((await api.get(`/vision-board?board_id=${boardId}`)).data); } catch (err) { console.debug(err); } };

  const openBoard = async (board) => {
    setActiveBoard(board);
    await fetchItems(board.id);
    setView('board');
  };

  const createBoard = async () => {
    if (!newBoardName.trim()) { toast.error('Nombre requerido'); return; }
    setShowNewBoard(false);
    setNewBoardName('');
    // Go to template chooser; we'll create board when template is selected
    setView('chooseTemplate');
    setActiveBoard({ name: newBoardName.trim(), template: null });
  };

  const selectTemplate = async (templateId) => {
    try {
      const { data } = await api.post('/vision-boards', { name: activeBoard.name, template: templateId });
      setBoards(prev => [...prev, data]);
      setActiveBoard(data);
      setItems([]);
      setView('board');
    } catch { toast.error('Error al crear tablero'); }
  };

  const deleteBoard = async (id) => {
    if (!window.confirm('¿Eliminar este tablero y todas sus imágenes?')) return;
    try {
      await api.delete(`/vision-boards/${id}`);
      toast.success('Tablero eliminado');
      // Reset state if currently viewing the deleted board
      if (activeBoard?.id === id) { setActiveBoard(null); setItems([]); setView('folder'); }
      // Refresh
      await fetchBoards();
    } catch (e) {
      console.error('deleteBoard error', e);
      toast.error('Error al eliminar el tablero');
    }
  };

  const renameBoard = async (id) => {
    if (!editBoardName.trim()) return;
    try {
      await api.put(`/vision-boards/${id}`, { name: editBoardName });
      setEditingBoardId(null); setEditBoardName(''); fetchBoards();
    } catch (err) { console.debug(err); }
  };

  const changeTemplate = async (templateId) => {
    try {
      await api.put(`/vision-boards/${activeBoard.id}`, { template: templateId });
      const updated = { ...activeBoard, template: templateId };
      setActiveBoard(updated);
      setBoards(prev => prev.map(b => b.id === updated.id ? updated : b));
      setView('board');
    } catch (err) { console.debug(err); }
  };

  const openSlotFiller = (idx) => { setSlotIndex(idx); setNewType('image'); setNewContent(''); setShowAdd(true); };
  const openFreeAdd = () => { setSlotIndex(null); setNewType('image'); setNewContent(''); setShowAdd(true); };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      const { data } = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setNewContent(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
      setNewType('image'); toast.success('Imagen subida');
    } catch { toast.error('Error al subir'); }
  };

  const submitItem = async () => {
    if (!newContent || !activeBoard?.id) return;
    try {
      const isFree = activeBoard.template === 'free';
      const position = isFree
        ? { x: 10 + Math.random() * 30, y: 10 + Math.random() * 30, w: 25, h: 25, free: true }
        : { slotIndex, offsetX: 0, offsetY: 0, zoom: 1 };
      await api.post('/vision-board', { type: newType, content: newContent, position, board_id: activeBoard.id });
      setShowAdd(false); setNewContent(''); setSlotIndex(null);
      fetchItems(activeBoard.id);
    } catch { toast.error('Error'); }
  };

  const deleteItem = async (id) => {
    try { await api.delete(`/vision-board/${id}`); fetchItems(activeBoard.id); } catch (err) { console.debug(err); }
  };

  const adjustSlotItem = async (id, field, delta) => {
    const it = items.find(x => x.id === id); if (!it) return;
    const pos = { ...(it.position || {}) };
    if (field === 'zoom') pos.zoom = Math.max(0.5, Math.min(3, (pos.zoom || 1) + delta));
    else if (field === 'x') pos.offsetX = (pos.offsetX || 0) + delta;
    else if (field === 'y') pos.offsetY = (pos.offsetY || 0) + delta;
    setItems(prev => prev.map(x => x.id === id ? { ...x, position: pos } : x));
    try { await api.put('/vision-board/positions', { positions: [{ id, ...pos }] }); } catch (err) { console.debug(err); }
  };

  // Free mode drag
  const onDragStart = (e, item) => {
    e.preventDefault();
    const rect = boardRef.current.getBoundingClientRect();
    const cx = e.clientX ?? e.touches?.[0]?.clientX;
    const cy = e.clientY ?? e.touches?.[0]?.clientY;
    setDragging({ id: item.id, offsetX: cx - rect.left - (item.position.x / 100) * rect.width, offsetY: cy - rect.top - (item.position.y / 100) * rect.height });
  };
  const onDragMove = (e) => {
    if (!dragging) return;
    const rect = boardRef.current.getBoundingClientRect();
    const cx = e.clientX ?? e.touches?.[0]?.clientX;
    const cy = e.clientY ?? e.touches?.[0]?.clientY;
    const x = ((cx - rect.left - dragging.offsetX) / rect.width) * 100;
    const y = ((cy - rect.top - dragging.offsetY) / rect.height) * 100;
    setItems(prev => prev.map(it => it.id === dragging.id ? { ...it, position: { ...it.position, x: Math.max(0, Math.min(80, x)), y: Math.max(0, Math.min(80, y)) } } : it));
  };
  const onDragEnd = async () => {
    if (!dragging) return;
    const item = items.find(it => it.id === dragging.id);
    setDragging(null);
    if (item) { try { await api.put('/vision-board/positions', { positions: [{ id: item.id, ...item.position }] }); } catch (err) { console.debug(err); } }
  };
  const resizeFree = async (id, delta) => {
    const it = items.find(x => x.id === id); if (!it) return;
    const w = Math.max(10, Math.min(60, (it.position.w || 25) + delta));
    setItems(prev => prev.map(x => x.id === id ? { ...x, position: { ...x.position, w, h: w } } : x));
    try { await api.put('/vision-board/positions', { positions: [{ id, x: it.position.x, y: it.position.y, w, h: w }] }); } catch (err) { console.debug(err); }
  };

  // ==== FOLDER SCREEN ====
  if (view === 'folder') {
    return (
      <div data-testid="vision-board-page" className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl sm:text-5xl font-light tracking-tight mb-1" style={{ color: theme.accent }}>Vision Boards</h1>
          </div>
          <Button data-testid="new-board-btn" onClick={() => setShowNewBoard(true)} className="text-white" style={{ background: theme.primary }}>
            <Plus size={16} className="mr-1" />Nuevo tablero
          </Button>
        </div>

        <Dialog open={showNewBoard} onOpenChange={setShowNewBoard}>
          <DialogContent style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
            <DialogHeader><DialogTitle style={{ color: theme.accent }}>Nuevo Vision Board</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input placeholder="Nombre del tablero (ej: Año 2026)" value={newBoardName} onChange={(e) => setNewBoardName(e.target.value)}
                className="text-white" style={{ background: theme.elevated, borderColor: theme.border }} />
              <Button data-testid="continue-template-btn" onClick={createBoard} className="w-full text-white" style={{ background: theme.primary }}>Continuar → Elegir plantilla</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Folder view */}
        <div className="rounded-xl p-4" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
          <div className="flex items-center gap-2 mb-4">
            <FolderOpen size={18} style={{ color: theme.accent }} />
            <span className="text-sm font-medium" style={{ color: theme.textPrimary }}>Mis tableros</span>
            <span className="text-xs" style={{ color: theme.textMuted }}>({boards.length})</span>
          </div>
          {boards.length === 0 ? (
            <div className="py-16 text-center">
              <Folder size={48} className="mx-auto mb-3" style={{ color: theme.textMuted }} />
              <p className="text-sm" style={{ color: theme.textMuted }}>Aún no tienes tableros. Crea el primero.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {boards.map(b => {
                const tpl = TEMPLATES.find(t => t.id === b.template);
                const preview = boardPreviews[b.id] || [];
                const hasItems = preview.length > 0;
                return (
                  <div key={b.id} data-testid={`board-${b.id}`} className="rounded-lg overflow-hidden group" style={{ background: theme.elevated, border: `1px solid ${theme.border}` }}>
                    <button onClick={() => openBoard(b)} className="w-full relative block" style={{ aspectRatio: '1/1', background: theme.bg }}>
                      {hasItems && tpl ? (
                        // Render mini-board with actual items in slots
                        <div className="absolute inset-0">
                          {tpl.slots.map((slot, i) => {
                            const item = preview.find(it => it.position?.slotIndex === i);
                            return (
                              <div key={i} className="absolute p-[2px]"
                                style={{ left: `${(slot.x / 12) * 100}%`, top: `${(slot.y / 12) * 100}%`, width: `${(slot.w / 12) * 100}%`, height: `${(slot.h / 12) * 100}%` }}>
                                {item ? (
                                  item.type === 'image' ? (
                                    <img src={item.content} alt="" className="w-full h-full object-cover rounded-sm" />
                                  ) : (
                                    <div className="w-full h-full rounded-sm flex items-center justify-center" style={{ background: theme.card }}>
                                      <p className="text-[7px] text-center px-1 truncate" style={{ color: theme.textSecondary }}>{item.content.slice(0, 20)}</p>
                                    </div>
                                  )
                                ) : (
                                  <div className="w-full h-full rounded-sm" style={{ background: `${theme.primary}15`, border: `1px dashed ${theme.border}` }} />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : hasItems && b.template === 'free' ? (
                        // Free mode: show items absolute
                        <div className="absolute inset-0">
                          {preview.slice(0, 8).map(item => {
                            const pos = item.position || { x: 20, y: 20, w: 25, h: 25 };
                            return (
                              <div key={item.id} className="absolute rounded-sm overflow-hidden"
                                style={{ left: `${pos.x}%`, top: `${pos.y}%`, width: `${pos.w}%`, height: `${pos.h}%` }}>
                                {item.type === 'image' ? <img src={item.content} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full" style={{ background: theme.card }} />}
                              </div>
                            );
                          })}
                        </div>
                      ) : tpl ? (
                        <div className="absolute inset-0">
                          <TemplatePreview slots={tpl.slots} color={theme.primary} bg={theme.bg} />
                        </div>
                      ) : (
                        <div className="flex items-center justify-center w-full h-full"><Move size={32} style={{ color: theme.accent }} /></div>
                      )}
                      {/* Count badge */}
                      {hasItems && (
                        <span className="absolute bottom-1 right-1 text-[9px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(0,0,0,0.7)', color: '#fff' }}>{preview.length}</span>
                      )}
                    </button>
                    <div className="p-3 flex items-center justify-between">
                      {editingBoardId === b.id ? (
                        <Input value={editBoardName} onChange={(e) => setEditBoardName(e.target.value)}
                          onBlur={() => renameBoard(b.id)} onKeyDown={(e) => e.key === 'Enter' && renameBoard(b.id)}
                          autoFocus className="text-white text-sm" style={{ background: theme.bg, borderColor: theme.border }} />
                      ) : (
                        <>
                          <button onClick={() => openBoard(b)} className="flex-1 text-left min-w-0">
                            <p className="text-sm font-medium truncate" style={{ color: theme.textPrimary }}>{b.name}</p>
                            <p className="text-[10px]" style={{ color: theme.textMuted }}>{tpl?.name || 'Libre'} · {preview.length} {preview.length === 1 ? 'elem.' : 'elems.'}</p>
                          </button>
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity ml-2">
                            <button onClick={() => { setEditingBoardId(b.id); setEditBoardName(b.name); }} style={{ color: theme.textMuted }}><Edit2 size={12} /></button>
                            <button onClick={() => deleteBoard(b.id)} style={{ color: theme.error }}><Trash2 size={12} /></button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ==== TEMPLATE CHOOSER ====
  if (view === 'chooseTemplate') {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <button onClick={() => setView('folder')} className="p-2 rounded-md" style={{ background: theme.card, border: `1px solid ${theme.border}`, color: theme.accent }}><ArrowLeft size={16} /></button>
          <div>
            <h1 className="text-3xl font-light tracking-tight" style={{ color: theme.accent }}>Elige una plantilla</h1>
            <p className="text-xs" style={{ color: theme.textMuted }}>Para: {activeBoard?.name}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {TEMPLATES.map(t => (
            <button key={t.id} data-testid={`template-${t.id}`} onClick={() => selectTemplate(t.id)}
              className="p-3 rounded-lg transition-all hover:scale-[1.02]"
              style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
              <TemplatePreview slots={t.slots} color={theme.primary} bg={theme.elevated} />
              <div className="mt-3 text-left">
                <p className="font-medium text-sm" style={{ color: theme.textPrimary }}>{t.name}</p>
                <p className="text-[11px]" style={{ color: theme.textMuted }}>{t.desc}</p>
              </div>
            </button>
          ))}
          <button data-testid="template-free" onClick={() => selectTemplate('free')}
            className="p-3 rounded-lg transition-all hover:scale-[1.02]"
            style={{ background: theme.card, border: `2px dashed ${theme.primary}` }}>
            <div className="w-full flex items-center justify-center" style={{ aspectRatio: '1/1', background: theme.elevated, borderRadius: '6px' }}>
              <div className="text-center"><Move size={32} style={{ color: theme.accent }} className="mx-auto" /><p className="text-xs mt-2" style={{ color: theme.textMuted }}>Arrastra libremente</p></div>
            </div>
            <div className="mt-3 text-left"><p className="font-medium text-sm" style={{ color: theme.accent }}>Libre</p><p className="text-[11px]" style={{ color: theme.textMuted }}>Drag & drop</p></div>
          </button>
        </div>
      </div>
    );
  }

  // ==== BOARD VIEW ====
  const selectedTemplate = TEMPLATES.find(t => t.id === activeBoard?.template);
  const templateSlots = selectedTemplate?.slots || [];
  const isFree = activeBoard?.template === 'free';
  const slotItems = templateSlots.map((slot, i) => ({ slot, item: items.find(it => it.position?.slotIndex === i), i }));

  return (
    <div data-testid="vision-board-page" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => setView('folder')} className="p-2 rounded-md" style={{ background: theme.card, border: `1px solid ${theme.border}`, color: theme.accent }}><ArrowLeft size={16} /></button>
          <div>
            <h1 className="text-3xl sm:text-4xl font-light tracking-tight" style={{ color: theme.accent }}>{activeBoard?.name}</h1>
            <p className="text-xs" style={{ color: theme.textMuted }}>{selectedTemplate?.name || 'Libre'}</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isFree && <Button onClick={openFreeAdd} className="text-white" style={{ background: theme.primary }}><Plus size={16} className="mr-1" />Agregar</Button>}
          {!isFree && (
            <Button onClick={toggleCollageMode} variant="outline" style={{ borderColor: theme.border, color: collageMode ? theme.accent : theme.textSecondary, background: collageMode ? `${theme.primary}20` : 'transparent' }}>
              <LayoutGrid size={14} className="mr-1" />{collageMode ? 'Modo collage' : 'Con margen'}
            </Button>
          )}
          <Button onClick={() => setView('chooseTemplate')} variant="outline" style={{ borderColor: theme.border, color: theme.textSecondary }}>
            <LayoutGrid size={14} className="mr-1" />Cambiar plantilla
          </Button>
          {activeBoard?.id && (
            <Button onClick={() => deleteBoard(activeBoard.id)} variant="outline" data-testid="delete-board-btn" style={{ borderColor: `${theme.error}50`, color: theme.error }}>
              <Trash2 size={14} className="mr-1" />Borrar tablero
            </Button>
          )}
        </div>
      </div>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
          <DialogHeader><DialogTitle style={{ color: theme.accent }}>{slotIndex !== null ? `Espacio ${slotIndex + 1}` : 'Nuevo elemento'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Button onClick={() => { setNewType('image'); setNewContent(''); }} variant="outline"
                style={{ background: newType === 'image' ? theme.primary : 'transparent', color: newType === 'image' ? '#fff' : theme.textMuted, border: `1px solid ${theme.border}` }}>
                <ImagePlus size={14} className="mr-1" />Imagen
              </Button>
              <Button onClick={() => { setNewType('text'); setNewContent(''); }} variant="outline"
                style={{ background: newType === 'text' ? theme.primary : 'transparent', color: newType === 'text' ? '#fff' : theme.textMuted, border: `1px solid ${theme.border}` }}>
                <Type size={14} className="mr-1" />Texto
              </Button>
            </div>
            {newType === 'image' ? (
              <>
                <label htmlFor="vb-up" className="flex items-center justify-center gap-2 p-8 border-2 border-dashed rounded-lg cursor-pointer" style={{ borderColor: theme.border }}>
                  <ImagePlus size={24} style={{ color: theme.accent }} />
                  <span style={{ color: theme.textMuted }}>Subir imagen</span>
                </label>
                <input id="vb-up" type="file" accept="image/*" onChange={handleUpload} className="hidden" />
                {newContent && <p className="text-sm" style={{ color: theme.success }}>✓ Lista</p>}
              </>
            ) : (
              <Textarea placeholder="Algo inspirador..." value={newContent} onChange={(e) => setNewContent(e.target.value)}
                className="min-h-[100px] text-white" style={{ background: theme.elevated, borderColor: theme.border }} />
            )}
            <Button onClick={submitItem} className="w-full text-white" style={{ background: theme.primary }}>Agregar</Button>
          </div>
        </DialogContent>
      </Dialog>

      <div ref={boardRef}
        onMouseMove={onDragMove} onMouseUp={onDragEnd} onMouseLeave={onDragEnd}
        onTouchMove={onDragMove} onTouchEnd={onDragEnd}
        className="relative rounded-lg overflow-hidden"
        style={{ background: theme.bg, border: `1px solid ${theme.border}`, aspectRatio: '12 / 9', userSelect: 'none' }}>
        {isFree ? (
          items.length === 0 ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center"><Move size={40} className="mx-auto mb-2" style={{ color: theme.textMuted }} /><p style={{ color: theme.textMuted }}>Agrega y arrastra elementos</p></div>
            </div>
          ) : items.map(item => {
            const pos = item.position || { x: 20, y: 20, w: 25, h: 25 };
            return (
              <div key={item.id} className="absolute rounded-md overflow-hidden group shadow-lg"
                style={{ left: `${pos.x}%`, top: `${pos.y}%`, width: `${pos.w}%`, height: `${pos.h}%`, cursor: dragging?.id === item.id ? 'grabbing' : 'grab', background: theme.elevated }}
                onMouseDown={(e) => onDragStart(e, item)} onTouchStart={(e) => onDragStart(e, item)}>
                {item.type === 'image' ? <img src={item.content} alt="" className="w-full h-full object-cover pointer-events-none" /> : <div className="w-full h-full flex items-center justify-center p-3" style={{ background: theme.card }}><p className="text-center text-sm" style={{ color: theme.textPrimary }}>{item.content}</p></div>}
                <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100">
                  <button onClick={(e) => { e.stopPropagation(); resizeFree(item.id, -5); }} className="w-6 h-6 rounded-full bg-black/70 text-white text-xs">−</button>
                  <button onClick={(e) => { e.stopPropagation(); resizeFree(item.id, 5); }} className="w-6 h-6 rounded-full bg-black/70 text-white text-xs">+</button>
                  <button onClick={(e) => { e.stopPropagation(); deleteItem(item.id); }} className="w-6 h-6 rounded-full bg-red-600/80 text-white flex items-center justify-center"><Trash2 size={10} /></button>
                </div>
              </div>
            );
          })
        ) : (
          slotItems.map(({ slot, item, i }) => (
            <div key={i} className={collageMode ? "absolute p-0" : "absolute p-1"}
              style={{ left: `${(slot.x / 12) * 100}%`, top: `${(slot.y / 12) * 100}%`, width: `${(slot.w / 12) * 100}%`, height: `${(slot.h / 12) * 100}%` }}>
              {item ? (
                <div className={`relative w-full h-full ${collageMode ? '' : 'rounded-md'} overflow-hidden group`} style={{ border: collageMode ? 'none' : `1px solid ${theme.border}`, background: theme.elevated }}>
                  {item.type === 'image' ? (
                    <img src={item.content} alt="" className="w-full h-full"
                      style={{
                        objectFit: item.position?.fit_mode === 'contain' ? 'contain' : 'cover',
                        background: item.position?.fit_mode === 'contain' ? theme.bg : 'transparent',
                        transform: `translate(${item.position?.offsetX || 0}px, ${item.position?.offsetY || 0}px) scale(${item.position?.zoom || 1})`,
                        transformOrigin: 'center',
                      }} />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center p-3" style={{ background: theme.card }}><p className="text-center text-sm" style={{ color: theme.textPrimary }}>{item.content}</p></div>
                  )}
                  {/* Adjustment controls */}
                  {item.type === 'image' && (
                    <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-1 opacity-0 group-hover:opacity-100 bg-black/60 rounded px-1 py-0.5">
                      <button data-testid={`fit-toggle-${i}`} onClick={() => toggleFitMode(item.id)} className="px-1.5 h-5 text-[9px] text-white"
                        title={item.position?.fit_mode === 'contain' ? 'Modo: ajustar' : 'Modo: rellenar'}>
                        {item.position?.fit_mode === 'contain' ? '⛶ ajustar' : '⛚ rellenar'}
                      </button>
                      <span className="text-white/30 text-[10px]">|</span>
                      <button data-testid={`adj-zoom-in-${i}`} onClick={() => adjustSlotItem(item.id, 'zoom', 0.1)} className="w-5 h-5 text-xs text-white">+</button>
                      <button onClick={() => adjustSlotItem(item.id, 'zoom', -0.1)} className="w-5 h-5 text-xs text-white">−</button>
                      <button onClick={() => adjustSlotItem(item.id, 'x', -5)} className="w-5 h-5 text-xs text-white">←</button>
                      <button onClick={() => adjustSlotItem(item.id, 'x', 5)} className="w-5 h-5 text-xs text-white">→</button>
                      <button onClick={() => adjustSlotItem(item.id, 'y', -5)} className="w-5 h-5 text-xs text-white">↑</button>
                      <button onClick={() => adjustSlotItem(item.id, 'y', 5)} className="w-5 h-5 text-xs text-white">↓</button>
                    </div>
                  )}
                  <button onClick={() => deleteItem(item.id)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-600/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <Trash2 size={12} />
                  </button>
                </div>
              ) : (
                <button data-testid={`vb-slot-${i}`} onClick={() => openSlotFiller(i)}
                  className="w-full h-full rounded-md flex items-center justify-center border-2 border-dashed hover:border-solid"
                  style={{ borderColor: theme.border, background: theme.card, color: theme.textMuted }}>
                  <Plus size={24} />
                </button>
              )}
            </div>
          ))
        )}
      </div>

      {!isFree && (
        <p className="text-xs text-center" style={{ color: theme.textMuted }}>
          Click en espacios vacíos para añadir • Hover sobre una imagen para ajustar posición (↑↓←→) y zoom (+/−)
        </p>
      )}
    </div>
  );
};

export default VisionBoard;
